/*
 * Copyright (c) 2025. Orion Analytics. All Rights Reserved
 */

import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';

const BondPriceSensitivity = () => {
  const [ytm, setYtm] = useState(5); // current YTM
  const [bondType, setBondType] = useState('Fixed');
  const [couponRate, setCouponRate] = useState(6); // %
  const [maturity, setMaturity] = useState(10); // years
  const [callPrice, setCallPrice] = useState(1050); // price in callable
  const [callAfter, setCallAfter] = useState(5); // callable after N years

  const [priceData, setPriceData] = useState([]);
  const [duration, setDuration] = useState(0);
  const [macDuration, setMacDuration] = useState(0);

  const faceValue = 1000;

  const calculatePrice = (rate, type = 'Fixed') => {
    rate = rate / 100;
    let price = 0;

    if (type === 'Zero') {
      return faceValue / Math.pow(1 + rate, maturity);
    }

    for (let t = 1; t <= maturity; t++) {
      const cashFlow = (t === maturity ? faceValue : 0) + faceValue * (couponRate / 100);
      if (type === 'Callable' && t === callAfter) {
        price += callPrice / Math.pow(1 + rate, t);
        break;
      }
      price += cashFlow / Math.pow(1 + rate, t);
    }

    return price;
  };

  const calculateDurations = (rate) => {
    rate = rate / 100;
    let macaulay = 0;
    let modDuration = 0;
    let price = 0;

    for (let t = 1; t <= maturity; t++) {
      const cf = (t === maturity ? faceValue : 0) + faceValue * (couponRate / 100);
      const pv = cf / Math.pow(1 + rate, t);
      price += pv;
      macaulay += t * pv;
    }

    macaulay = macaulay / price;
    modDuration = macaulay / (1 + rate);

    return { macaulay, modDuration };
  };

  useEffect(() => {
    const rates = Array.from({ length: 41 }, (_, i) => i); // 0% to 40%
    const prices = rates.map(r => calculatePrice(r, bondType));

    setPriceData({ x: rates, y: prices });

    const { macaulay, modDuration } = calculateDurations(ytm);
    setDuration(modDuration.toFixed(2));
    setMacDuration(macaulay.toFixed(2));
  }, [ytm, bondType, couponRate, maturity, callPrice, callAfter]);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h2 className="text-xl font-bold mb-4">📉 Bond Price vs Interest Rate (YTM)</h2>

      {/* Bond Type Toggle */}
      <div className="mb-4">
        <label className="mr-2 font-semibold">Bond Type:</label>
        {['Fixed', 'Zero', 'Callable'].map(type => (
          <label key={type} className="mr-4">
            <input
              type="radio"
              value={type}
              checked={bondType === type}
              onChange={(e) => setBondType(e.target.value)}
              className="mr-1"
            />
            {type}
          </label>
        ))}
      </div>

      {/* Sliders */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block font-medium">YTM: {ytm}%</label>
          <input
            type="range"
            min="0"
            max="40"
            step="0.1"
            value={ytm}
            onChange={(e) => setYtm(parseFloat(e.target.value))}
            className="w-full"
          />
        </div>

        <div>
          <label className="block font-medium">Coupon Rate: {couponRate}%</label>
          <input
            type="range"
            min="0"
            max="30"
            step="0.1"
            value={couponRate}
            onChange={(e) => setCouponRate(parseFloat(e.target.value))}
            className="w-full"
          />
        </div>

        <div>
          <label className="block font-medium">Maturity: {maturity} years</label>
          <input
            type="range"
            min="1"
            max="40"
            step="1"
            value={maturity}
            onChange={(e) => setMaturity(parseInt(e.target.value))}
            className="w-full"
          />
        </div>

        {bondType === 'Callable' && (
          <>
            <div>
              <label className="block font-medium">Call Price: ₹{callPrice}</label>
              <input
                type="range"
                min="900"
                max="1200"
                step="10"
                value={callPrice}
                onChange={(e) => setCallPrice(parseInt(e.target.value))}
                className="w-full"
              />
            </div>

            <div>
              <label className="block font-medium">Call After: {callAfter} years</label>
              <input
                type="range"
                min="1"
                max={maturity}
                step="1"
                value={callAfter}
                onChange={(e) => setCallAfter(parseInt(e.target.value))}
                className="w-full"
              />
            </div>
          </>
        )}
      </div>

      {/* Plot */}
      <Plot
        data={[
          {
            x: priceData.x,
            y: priceData.y,
            type: 'scatter',
            mode: 'lines+markers',
            marker: { color: 'blue' },
            name: 'Bond Price'
          },
          {
            x: [ytm],
            y: [calculatePrice(ytm, bondType)],
            type: 'scatter',
            mode: 'markers+text',
            marker: { color: 'red', size: 10 },
            name: 'Selected YTM',
            text: [`₹${calculatePrice(ytm, bondType).toFixed(2)}`],
            textposition: 'top center'
          }
        ]}
        layout={{
          width: 850,
          height: 500,
          title: 'Bond Price Sensitivity to YTM',
          xaxis: { title: 'Yield to Maturity (%)' },
          yaxis: { title: 'Bond Price (₹)' },
        }}
      />

      <div className="mt-6">
        <p><strong>Macaulay Duration:</strong> {macDuration} years</p>
        <p><strong>Modified Duration:</strong> {duration} years</p>
      </div>
    </div>
  );
};

export default BondPriceSensitivity;
